import { useUIv2 } from '@/hooks/useUIv2'
import DigitalCoworkerV2 from './DigitalCoworkerV2'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import PageLayout from '@/components/layout/PageLayout'
import { Search, List, Grid, Plus, MessageSquare, Phone, MoreVertical, Loader2, FileText, Users } from 'lucide-react'
import { apiService } from '@/service/api'
import type { DigitalCoworkerRecord } from '@/service/api'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { DigitalCoworkerChatSheet } from './DigitalCoworkerChatSheet'
import { DigitalCoworkerDetailsDialog } from './DigitalCoworkerDetailsDialog'
import DigitalCoworkerCallModal from '@/components/DigitalCoworkerCallModal'
import { coworkerAvatarUrl } from '@/utils/coworkerAvatar'

type StatusFilter = 'all' | 'pending' | 'training' | 'active'

interface PageToolsProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  statusFilter: StatusFilter
  onStatusChange: (value: StatusFilter) => void
  viewMode: 'list' | 'grid'
  onViewModeChange: (mode: 'list' | 'grid') => void
  onCreateClick: () => void
  labels: {
    searchPlaceholder: string
    statusAll: string
    statusActive: string
    statusTraining: string
    statusPending: string
    newButton: string
  }
}

const PageTools = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  viewMode,
  onViewModeChange,
  onCreateClick,
  labels,
}: PageToolsProps) => (
  <div className="flex items-center gap-3">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
      <Input
        type="text"
        placeholder={labels.searchPlaceholder}
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="pl-10 w-64"
      />
    </div>

    <Select
      value={statusFilter}
      onValueChange={(value) => onStatusChange(value as StatusFilter)}
    >
      <SelectTrigger className="w-40">
        <SelectValue placeholder={labels.statusAll} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{labels.statusAll}</SelectItem>
        <SelectItem value="active">{labels.statusActive}</SelectItem>
        <SelectItem value="training">{labels.statusTraining}</SelectItem>
        <SelectItem value="pending">{labels.statusPending}</SelectItem>
      </SelectContent>
    </Select>

    <div className="flex items-center border rounded-md">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onViewModeChange('list')}
        className={viewMode === 'list' ? 'bg-gray-100 dark:bg-dark-accent' : ''}
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onViewModeChange('grid')}
        className={viewMode === 'grid' ? 'bg-gray-100 dark:bg-dark-accent' : ''}
      >
        <Grid className="h-4 w-4" />
      </Button>
    </div>

    <Button className="gap-2" onClick={onCreateClick} data-tour-id="dc-create">
      <Plus className="h-4 w-4" />
      {labels.newButton}
    </Button>
  </div>
)

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800'
    case 'training':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800'
    case 'pending':
      return 'bg-gray-100 dark:bg-dark-accent text-gray-800 dark:text-dark-text border-gray-200 dark:border-dark-border'
    default:
      return 'bg-gray-100 dark:bg-dark-accent text-gray-800 dark:text-dark-text border-gray-200 dark:border-dark-border'
  }
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

const DigitalCoworkerPage = () => {
  const isV2 = useUIv2()
  if (isV2) return <DigitalCoworkerV2 />
  return <DigitalCoworkerLegacy />
}

const DigitalCoworkerLegacy = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')
  const [coworkers, setCoworkers] = useState<DigitalCoworkerRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [chatCoworker, setChatCoworker] = useState<DigitalCoworkerRecord | null>(null)
  const [callCoworker, setCallCoworker] = useState<DigitalCoworkerRecord | null>(null)
  const [deleteCoworker, setDeleteCoworker] = useState<{ id: string; name: string } | null>(null)
  const [detailsCoworker, setDetailsCoworker] = useState<DigitalCoworkerRecord | null>(null)

  // Monotonically-increasing request ID to discard out-of-order responses
  const requestIdRef = useRef(0)

  const fetchCoworkers = useCallback(async (showLoader = true, signal?: AbortSignal) => {
    // Only user-initiated fetches increment the counter; polls read current
    // value so they can't invalidate an in-flight user fetch's loading state.
    const reqId = showLoader ? ++requestIdRef.current : requestIdRef.current
    if (showLoader) setIsLoading(true)
    try {
      const result = await apiService.listDigitalCoworkers({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined,
      }, { signal })
      if (signal?.aborted || reqId !== requestIdRef.current) return
      setCoworkers(result.digital_coworkers)
    } catch (error) {
      if ((error as Error).name === 'AbortError') return
      if (reqId !== requestIdRef.current) return
      console.error('Failed to fetch digital coworkers:', error)
      if (showLoader) toast.error(t('digital_coworker:toast.load_error'))
    } finally {
      if (showLoader && reqId === requestIdRef.current) setIsLoading(false)
    }
  }, [statusFilter, searchTerm, t])

  // Keep a ref so the polling interval always calls the latest version
  // without resetting the timer on every keystroke / filter change.
  const fetchCoworkersRef = useRef(fetchCoworkers)
  useEffect(() => { fetchCoworkersRef.current = fetchCoworkers }, [fetchCoworkers])

  // Initial fetch (debounced for filter/search changes)
  useEffect(() => {
    const controller = new AbortController()
    const timer = setTimeout(() => {
      void fetchCoworkers(true, controller.signal)
    }, 300)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [fetchCoworkers])

  // Poll every 5s to pick up status transitions (pending → training → active)
  useEffect(() => {
    const controller = new AbortController()
    const interval = setInterval(() => {
      void fetchCoworkersRef.current(false, controller.signal)
    }, 5000)
    return () => {
      clearInterval(interval)
      controller.abort()
    }
  }, [])

  const handleDelete = async () => {
    if (!deleteCoworker) return
    const { id, name } = deleteCoworker
    setDeleteCoworker(null)
    try {
      await apiService.deleteDigitalCoworker(id)
      toast.success(t('digital_coworker:toast.deleted', { name }))
      void fetchCoworkers()
    } catch (error) {
      console.error('Failed to delete digital coworker:', error)
      toast.error(t('digital_coworker:toast.delete_error'))
    }
  }

  const getStatusLabel = (status: string) => {
    const key = status as 'active' | 'training' | 'pending'
    return t(`digital_coworker:status_filter.${key}`)
  }

  return (
    <PageLayout
      title={t('digital_coworker:page_title')}
      pageTools={() => (
        <PageTools
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onCreateClick={() => navigate('/digital-coworker/new')}
          labels={{
            searchPlaceholder: t('digital_coworker:search_placeholder'),
            statusAll: t('digital_coworker:status_filter.all'),
            statusActive: t('digital_coworker:status_filter.active'),
            statusTraining: t('digital_coworker:status_filter.training'),
            statusPending: t('digital_coworker:status_filter.pending'),
            newButton: t('digital_coworker:new_button'),
          }}
        />
      )}
    >
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
          </div>
        ) : coworkers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-dark-text-muted text-sm">{t('digital_coworker:empty_state.message')}</p>
            <Button
              className="mt-4 gap-2"
              onClick={() => navigate('/digital-coworker/new')}
            >
              <Plus className="h-4 w-4" />
              {t('digital_coworker:empty_state.create_button')}
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('digital_coworker:table.name')}</TableHead>
                <TableHead>{t('digital_coworker:table.role')}</TableHead>
                <TableHead>{t('digital_coworker:table.status')}</TableHead>
                <TableHead>{t('digital_coworker:table.documents')}</TableHead>
                <TableHead>{t('digital_coworker:table.interviews')}</TableHead>
                <TableHead>{t('digital_coworker:table.integration')}</TableHead>
                <TableHead>{t('digital_coworker:table.created')}</TableHead>
                <TableHead>{t('digital_coworker:table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coworkers.map((coworker) => {
                const avatarUrl = coworkerAvatarUrl(coworker)
                return (
                <TableRow key={coworker.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center font-semibold text-orange-600 text-sm overflow-hidden">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={coworker.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          getInitials(coworker.name)
                        )}
                      </div>
                      <div>
                        <span className="font-medium">{coworker.name}</span>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{coworker.department}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-gray-400">{coworker.job_title}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getStatusColor(coworker.status)}>
                      {getStatusLabel(coworker.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm">
                      <FileText className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                      <span>{coworker.indexed_documents_count}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm">
                      <Users className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                      <span>{coworker.interviews_count}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {coworker.google_integration.is_connected ? (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {t('digital_coworker:integration_connected', {
                          email: coworker.google_integration.email ?? t('common:general.unknown'),
                        })}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">
                        {t('digital_coworker:integration_none')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-gray-400 text-sm">
                    {new Date(coworker.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title={`Chat with ${coworker.name}`}
                        onClick={() => setChatCoworker(coworker)}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title={`Start a call with ${coworker.name}`}
                        onClick={() => setCallCoworker(coworker)}
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailsCoworker(coworker)}>
                            {t('digital_coworker:dropdown.view_details')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => navigate('/digital-coworker/new', {
                              state: {
                                coworkerId: coworker.id,
                                formData: {
                                  type: coworker.type,
                                  name: coworker.name,
                                  jobTitle: coworker.job_title,
                                  department: coworker.department,
                                  expertise: coworker.expertise || '',
                                },
                              },
                            })}
                            disabled={coworker.type === 'colleague'}
                          >
                            {t('digital_coworker:dropdown.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => setDeleteCoworker({ id: coworker.id, name: coworker.name })}
                          >
                            {t('digital_coworker:dropdown.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <DigitalCoworkerChatSheet
        coworker={chatCoworker}
        isOpen={chatCoworker !== null}
        onClose={() => setChatCoworker(null)}
      />

      <DigitalCoworkerCallModal
        coworker={callCoworker}
        isOpen={callCoworker !== null}
        onClose={() => setCallCoworker(null)}
      />

      <DigitalCoworkerDetailsDialog
        coworker={detailsCoworker}
        onOpenChange={(open) => { if (!open) setDetailsCoworker(null) }}
      />

      <AlertDialog open={!!deleteCoworker} onOpenChange={(open) => { if (!open) setDeleteCoworker(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('digital_coworker:delete_dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('digital_coworker:delete_dialog.description', { name: deleteCoworker?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('digital_coworker:delete_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('digital_coworker:delete_dialog.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  )
}

export default DigitalCoworkerPage
